import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, DatePicker, Input } from 'antd';
import type { Dayjs } from 'dayjs';
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { SelectedAssignment } from '../../../../pages/lms/assignment-mgmt/CreateAssignment';
import TextWinds from '../../../common/TextWinds';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../store/hook';
import {
  AssignmentClassItem,
  AssignmentStudentItem,
} from '../../../../constants/types/lms/recitation/type';
import {
  getAssignmentClassGroupList,
  getAssignmentStudentList,
  getVoucherBalanceList,
  postAssignmentsToStudents,
} from '../../../../api/utils/lms/recitation/api';
import {
  openAlert,
  openConfirm,
  openModal,
} from '../../../../store/features/modalSlice';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { LoadingSpinner } from '../../../Loading/LoadingSpinner';
import dayjs from 'dayjs';
import { AxiosError } from 'axios';
import { getVoucherProductList } from '../../../../api/utils/lms/voucher/api';
import {
  AssignmentType,
  SUBJECT_LABEL_MAP,
} from '../../../../constants/types/lms/type';
import ModalAlert from '../../../modal/ModalAlert';
import { numberWithCommas } from '../../../../api/utils/format/numbers';

interface StudentSelectionPanelProps {
  selectedAssignments: SelectedAssignment[];
  onInit: () => void;
  onBack: () => void;
}

const ALL_CLASS_ID = 'ALL';

type ClassItemWithStudentCount = AssignmentClassItem & {
  studentCount: number;
};

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 내주기 페이지 > 콘텐츠 영역 > 학생 선택 판넬
 */
const StudentSelectionPanel = ({
  selectedAssignments,
  onInit,
  onBack,
}: StudentSelectionPanelProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [voucherProductList, setVoucherProductList] = useState<
    { voucherType: AssignmentType; productId: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [classList, setClassList] = useState<ClassItemWithStudentCount[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | string>(
    ALL_CLASS_ID,
  );
  const [studentList, setStudentList] = useState<AssignmentStudentItem[]>([]);
  const [studentMapByClassId, setStudentMapByClassId] = useState<
    Record<string | number, AssignmentStudentItem[]>
  >({});
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(
    new Set(),
  );

  const [keyword, setKeyword] = useState('');
  const [deadline, setDeadline] = useState<Dayjs | null>(null);

  const [isPending, setIsPending] = useState<boolean>(false);

  useEffect(() => {
    if (!classId) return;

    const fetchVoucherProductList = async () => {
      try {
        const response = await getVoucherProductList();

        setVoucherProductList(response.items);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      }
    };

    const fetchAssignmentClassList = async () => {
      try {
        setIsLoading(true);

        const response = await getAssignmentClassGroupList({
          classId,
          isActive: true, // 활성화된 반만 조회
        });

        const classes = response ?? [];

        const students = await getAssignmentStudentList({
          classId,
          classGroupId: [],
        });

        const studentsByClassGroupId = (students ?? []).reduce<
          Record<number, AssignmentStudentItem[]>
        >((acc, student) => {
          const classGroupId = student.classInfos[0]?.classGroup?.id;

          if (classGroupId == null) {
            return acc;
          }

          if (!acc[classGroupId]) {
            acc[classGroupId] = [];
          }

          acc[classGroupId].push(student);

          return acc;
        }, {});

        const nextStudentMap: Record<number | 'ALL', AssignmentStudentItem[]> =
          {
            [ALL_CLASS_ID]: students ?? [],
          };

        const classesWithStudentCount = classes.map((classItem) => {
          const classStudents = studentsByClassGroupId[classItem.id] ?? [];

          nextStudentMap[classItem.id] = classStudents;

          return {
            ...classItem,
            studentCount: classStudents.length,
          };
        });

        setClassList(classesWithStudentCount);
        setStudentMapByClassId(nextStudentMap);
        setSelectedClassId(ALL_CLASS_ID);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchVoucherProductList();
    fetchAssignmentClassList();
  }, [classId, dispatch]);

  useEffect(() => {
    if (!selectedClassId) return;

    if (selectedClassId === ALL_CLASS_ID) {
      setStudentList(studentMapByClassId[selectedClassId] ?? []);
      return;
    }

    setStudentList(studentMapByClassId[Number(selectedClassId)] ?? []);
  }, [selectedClassId, studentMapByClassId]);

  const classOptions = useMemo(() => {
    const totalStudentCount = (studentMapByClassId[selectedClassId] ?? [])
      .length;

    return [
      {
        id: ALL_CLASS_ID,
        name: '전체',
        studentCount: totalStudentCount,
      },
      ...classList,
    ];
  }, [classList]);

  const filteredStudents = useMemo(() => {
    return studentList.filter((student) => {
      const matchesKeyword = student.name.includes(keyword);

      return matchesKeyword;
    });
  }, [studentList, keyword]);

  const selectedCount = selectedStudentIds.size;

  const isAllSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((student) => selectedStudentIds.has(student.id));

  const isPartiallySelected =
    filteredStudents.some((student) => selectedStudentIds.has(student.id)) &&
    !isAllSelected;

  const deadlineText = deadline ? deadline.format('M/D 23:59') : '미선택';

  const handleAllSelect = () => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);

      if (isAllSelected) {
        filteredStudents.forEach((student) => next.delete(student.id));
      } else {
        filteredStudents.forEach((student) => next.add(student.id));
      }

      return next;
    });
  };

  const handleStudentToggle = (studentId: number) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);

      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }

      return next;
    });
  };

  const renderErrorMessage = (error: unknown) => (
    <div className="flex flex-col md:my-3">
      {(getErrorMessages(error) as string)
        .split('.')
        .map((sentence) => sentence.trim())
        .filter(Boolean)
        .map((sentence, index) => (
          <div key={`${sentence}-${index}`} className="text-base">
            {sentence}.
          </div>
        ))}
    </div>
  );

  const openVoucherPurchasePage = (voucherType?: string) => {
    const voucherProduct = voucherProductList.find(
      (product) => product.voucherType === voucherType,
    );

    const appUrl = process.env.REACT_APP_JEBS_APP_URL;
    const productId = voucherProduct?.productId;

    if (!appUrl || !productId) {
      dispatch(
        openModal({
          body: (
            <ModalAlert message="이용권 구매를 할 수 없습니다. 관리자에게 문의하세요." />
          ),
        }),
      );
      return;
    }

    window.open(
      `${appUrl}/shop/details/${productId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const submitAssignments = async ({
    studentIds,
    confirmDuplicates = false,
  }: {
    studentIds: number[];
    confirmDuplicates?: boolean;
  }) => {
    try {
      setIsPending(true);

      await postAssignmentsToStudents({
        classId,
        toDoIds: selectedAssignments.map((assignment) => assignment.id),
        studentIds,
        toDate: deadline!.format('YYYY-MM-DD'),
        confirmDuplicates,
      });

      dispatch(
        openAlert({
          message: '과제 내주기가 성공적으로 완료되었습니다.',
        }),
      );

      onInit();
      onBack();
    } catch (error) {
      const responseData = (
        error as AxiosError<{
          code?: string;
          details?: {
            voucherType?: string;
          }[];
        }>
      ).response?.data;

      if (!responseData) {
        dispatch(openAlert({ message: '잠시 후에 다시 시도해주세요.' }));
        return;
      }

      if (responseData.code === 'LMS_TODO_DUPLICATE_ASSIGNMENT') {
        dispatch(
          openConfirm({
            message: (
              <>
                {renderErrorMessage(error)}

                <div className="py-3 text-sm text-gray7">
                  * 이미 내준 과제를 다시 배정해도 이용권이 차감됩니다.
                </div>

                <div className="text-base font-bold">
                  과제를 내주시겠습니까?
                </div>
              </>
            ),
            confirmText: '확인',
            cancelText: '닫기',
            onConfirm: () => {
              void submitAssignments({
                studentIds,
                confirmDuplicates: true,
              });
            },
          }),
        );
        return;
      }

      if (responseData.code === 'LMS_TODO_VOUCHER_INSUFFICIENT') {
        const voucherType = responseData.details?.[0]?.voucherType;

        dispatch(
          openConfirm({
            message: renderErrorMessage(error),
            confirmText: '이용권 구매하기',
            cancelText: '닫기',
            onConfirm: () => {
              openVoucherPurchasePage(voucherType);
            },
          }),
        );
        return;
      }

      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsPending(false);
    }
  };

  const handleSendAssignmentClick = async () => {
    const selectedStudentIdsArray = studentList
      .filter((student) => selectedStudentIds.has(student.id))
      .map((student) => student.id);

    if (selectedStudentIdsArray.length === 0 || !deadline) return;

    try {
      setIsPending(true);

      const response = await getVoucherBalanceList();

      const targetCountMapByType = selectedAssignments.reduce(
        (acc, assignment) => {
          acc.set(assignment.type, (acc.get(assignment.type) ?? 0) + 1);

          return acc;
        },
        new Map<AssignmentType, number>(),
      );

      const selectedAssignmentGroups = Array.from(
        targetCountMapByType,
        ([type, assignmentCount]) => {
          const matchedBalance = response.items.find(
            (balance) => balance.type === type,
          );

          return {
            type,
            targetCount: assignmentCount * selectedStudentIdsArray.length,
            remaining: matchedBalance?.remaining ?? 0,
          };
        },
      );

      dispatch(
        openConfirm({
          message: (
            <div className="flex flex-col gap-3">
              <div>
                <div className="text-base font-bold">이용권 차감 안내</div>

                <div className="mt-1 text-sm text-gray7">
                  과제를 내주면 아래 이용권이 차감됩니다.
                </div>
              </div>

              <div className="flex flex-col gap-1.5 rounded-lg bg-gray0 px-3 py-3">
                {selectedAssignmentGroups.map(
                  ({ type, targetCount, remaining }) => (
                    <div
                      key={type}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span className="text-gray8">
                        {SUBJECT_LABEL_MAP[type]} 이용권
                      </span>

                      <span className="font-bold text-gray9">
                        {numberWithCommas(targetCount)}개 차감 · 현재{' '}
                        {numberWithCommas(remaining)}개
                      </span>
                    </div>
                  ),
                )}
              </div>

              <div className="text-base font-bold">과제를 내주시겠습니까?</div>
            </div>
          ),
          confirmText: '확인',
          cancelText: '닫기',
          onConfirm: () => {
            void submitAssignments({
              studentIds: selectedStudentIdsArray,
            });
          },
        }),
      );
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsPending(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[300px_1fr]">
        <div className="flex flex-col gap-5">
          <TextWinds type="title_h3">반 선택</TextWinds>

          <aside className="overflow-hidden rounded-md border border-gray3 bg-white">
            <div className="flex flex-col">
              {classOptions.map((classItem) => {
                const isSelected = selectedClassId === classItem.id;

                return (
                  <Button
                    key={classItem.id}
                    type="text"
                    className={[
                      'flex h-auto w-full items-center justify-between rounded-none border-0 border-b border-gray3 px-4 py-4 text-left last:border-b-0',
                      isSelected
                        ? '!border-purple2 !bg-purple3'
                        : '!bg-white hover:!bg-gray0',
                    ].join(' ')}
                    onClick={() => setSelectedClassId(classItem.id)}
                  >
                    <TextWinds type="content_body2" color="gray9">
                      {classItem.name}
                    </TextWinds>

                    <TextWinds type="content_body2" color="gray7">
                      {classItem.studentCount}명
                    </TextWinds>
                  </Button>
                );
              })}
            </div>
          </aside>
        </div>

        {/* 모바일 구분선 */}
        <div className="h-px bg-gray3 xl:hidden" />

        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between">
            <TextWinds type="title_h3">학생 선택</TextWinds>

            <TextWinds type="caption1" color="gray7">
              총 {filteredStudents.length}명
            </TextWinds>
          </div>

          <section className="overflow-hidden rounded-xl border border-gray3 bg-white">
            <div className="border-b border-gray3 bg-white px-4 py-3">
              <Input
                value={keyword}
                placeholder="이름 검색..."
                prefix={
                  <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-gray6" />
                }
                className="rounded-lg"
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>

            <div className="flex h-11 items-center justify-between border-b border-gray3 bg-gray0 px-4">
              <Checkbox
                checked={isAllSelected}
                indeterminate={isPartiallySelected}
                onChange={handleAllSelect}
              >
                <TextWinds type="caption1" color="gray8">
                  전체 선택
                </TextWinds>
              </Checkbox>

              <TextWinds type="caption1" color="gray9">
                <span className="font-semibold text-green-700">
                  {selectedCount}명
                </span>{' '}
                선택됨
              </TextWinds>
            </div>

            <div className="flex h-[280px] flex-col overflow-y-auto xl:h-[360px]">
              {filteredStudents.map((student, index) => {
                const isSelected = selectedStudentIds.has(student.id);

                return (
                  <label
                    key={student.id}
                    className="flex cursor-pointer items-center justify-between border-b border-gray2 px-5 py-4 hover:bg-gray0"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <div className="min-w-0">
                        <TextWinds type="content_body2" color="gray9">
                          {student.name}
                        </TextWinds>

                        <TextWinds
                          type="caption1"
                          color="gray7"
                          className="mt-0.5 block"
                        >
                          {student.uid} · {student.id}
                        </TextWinds>
                      </div>
                    </div>

                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleStudentToggle(student.id)}
                    />
                  </label>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {/* 모바일 구분선 */}
      <div className="my-5 h-px bg-gray3 xl:hidden" />

      <section className="mt-4 rounded-xl border border-gray3 bg-white p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="flex w-[120px] shrink-0 items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-blue-500" />

            <TextWinds type="content_body3" color="gray8">
              마감기한
            </TextWinds>
          </div>

          <DatePicker
            value={deadline}
            className="w-full max-w-[300px] rounded-lg"
            placeholder="마감기한 선택"
            format="YYYY-MM-DD"
            onChange={(value) => setDeadline(value)}
            disabledDate={(current) => {
              return current && current < dayjs().startOf('day');
            }}
          />
        </div>
      </section>

      <div className="mt-5 flex flex-col gap-3 border-t border-gray3 pt-4 xl:flex-row xl:items-center xl:justify-between">
        <TextWinds type="content_body2" color="gray9">
          총 <span className="font-semibold">{selectedCount}</span>명 ·
          마감&nbsp;
          <span className="font-semibold">{deadlineText}</span>
        </TextWinds>

        <div className="flex justify-end gap-3">
          <Button
            type="default"
            className="flex items-center gap-2 rounded-lg px-5"
            onClick={onBack}
          >
            ← 이전
          </Button>

          <Button
            type="primary"
            disabled={selectedCount === 0 || !deadline || isPending}
            className={[
              'flex items-center gap-2 rounded-lg px-5',
              selectedCount === 0 || !deadline
                ? '!cursor-not-allowed !border-gray3 !bg-gray1'
                : '!border-purple5 !bg-purple5 hover:!bg-purple4',
            ].join(' ')}
            onClick={() => handleSendAssignmentClick()}
          >
            <PaperAirplaneIcon className="h-4 w-4" />
            과제 내주기
          </Button>
        </div>
      </div>
    </section>
  );
};

export default StudentSelectionPanel;
